import {
  Box,
  Button,
  Center,
  Flex,
  Group,
  Image,
  Stack,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useTranslation } from "react-i18next";
import { FaDownload, FaRegCopy, FaShareSquare } from "react-icons/fa";
import { useEffect, useState } from "react";
import { showNotification } from "@mantine/notifications";

const useStyles = createStyles((theme) => ({
  certificadoContainer: {
    width: 1280,
    height: 720,
    border: `1px solid ${theme.colors.gray[4]}`,
    position: "relative",
    overflow: "hidden",
  },
  decoracionIzquierda: {
    position: "absolute",
  },
  decoracionDerechaInferior: {
    position: "absolute",
    bottom: 0,
    right: 0,
  },
  content: {
    position: "absolute",
    display: "flex",
    flexDirection: "column",
    gap: 20,
    top: 75,
    left: 400,
    justifyContent: "center",
    alignItems: "center",
    width: 800,
  },
  certificadoEntregadoAText: {
    fontFamily: "Kollektif",
    textTransform: "uppercase",
    fontSize: 30,
    fontWeight: 700,
    letterSpacing: 2,
    marginTop: 20,
  },
  nombreEmpresa: {
    fontFamily: "Kollektif",
    textTransform: "uppercase",
    fontSize: 50,
    fontWeight: 700,
    letterSpacing: 1.5,
  },
  descripcion: {
    fontFamily: "Montserrat",
    fontSize: 20,
    textAlign: "center",
  },
  nombreFirma: {
    fontFamily: "Montserrat",
    fontWeight: 900,
    fontSize: 22,
    marginTop: -10,
  },
  detalleFirma: {
    fontFamily: "Montserrat",
    fontSize: 22,
    marginTop: -20,
  },
  fecha: {
    position: "absolute",
    top: 625,
    fontFamily: "Montserrat",
    fontSize: 18,
    fontWeight: 900,
  },
  downloadCertificate: {
    position: "absolute",
    top: 100,
    right: theme.spacing.md,
    zIndex: 1,
  },
  shareLinkedin: {
    position: "absolute",
    top: 50,
    right: theme.spacing.md,
    zIndex: 1,
  },
}));

type Props = {
  nombreEmpresa: string;
  nombreCurso: string;
  customHeader?: string;
  fecha?: string;
  triggerDownload?: boolean;
  children: React.ReactNode;
};

export const CertificadoShell = ({
  nombreEmpresa,
  nombreCurso,
  customHeader,
  fecha,
  triggerDownload,
  children,
}: Props) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [fechaStyle, setFechaStyle] = useState({ left: "1050px" });

  useEffect(() => {
    if (!fecha || (fecha && fecha.length <= 10)) {
      setFechaStyle({ left: "1150px" }); // Ajusta el valor de "left" según lo que necesites
    } else {
      setFechaStyle({ left: "1050px" });
    }
    if (triggerDownload) {
      downloadCertificate();
    }
  }, [fecha]);

  const downloadCertificate = async () => {
    const doc = new jsPDF({
      orientation: "l",
      unit: "mm",
      format: [1280, 720],
      putOnlyUsedFonts: true,
    });

    const content = document.getElementById("certificado")!;
    await html2canvas(content, {
      scale: 2,
      logging: true,
      useCORS: true,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      doc.addImage(imgData, "png", 0, 0, 1280, 720);
    });
    doc.save(`Certificado-${nombreEmpresa}-${nombreCurso}.pdf`);
  };
  
  const shareOnLinkedIn = async () => {
    const content = document.getElementById("certificado")!;
    await html2canvas(content, {
      scale: 2,
      logging: true,
      useCORS: true,
    }).then(async (canvas) => {
      const imgData = canvas.toDataURL("image/png");
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": await fetch(imgData).then((r) => r.blob()) }),
      ]);
      showNotification({
        title: "Imagen copiada",
        message: "Presiona Ctrl + V para pegar en LinkedIn.",
        color: "blue",
        icon: <FaRegCopy />,
      });
      setTimeout(() => {
        window.open("https://www.linkedin.com/sharing/share-offsite", "_blank");
      }, 1000);
    });
  };

  return (
    <Center my="md">
      <Box w="100%">
        <div className={classes.certificadoContainer} id="certificado">
          <Image
            src="/img/certificados/decoracion-izquierda.png"
            alt="Decoración izquierda"
            className={classes.decoracionIzquierda}
            w={380}
          />
          <Image
            src="/img/certificados/decoracion-derecha-inferior.png"
            alt="Decoración derecha inferior"
            className={classes.decoracionDerechaInferior}
            w={250}
          />
          <div className={classes.content}>
            <Image
              src="/img/certificados/elsa-black.png"
              alt="Elsa"
              w={250}
            />
            <div className={classes.certificadoEntregadoAText}>
              {customHeader
                ? customHeader
                : "constancia corporativa otorgada a:"}
            </div>
            <div className={classes.nombreEmpresa}>{nombreEmpresa}</div>
            <div className={classes.descripcion}>
              {children}
              <Stack gap="md" align="center" mt="md">
                <Image
                  src={"/img/certificados/firma.png"}
                  alt="Firma"
                  w={225}
                />
                <div className={classes.nombreFirma}>Marlene Molero</div>
                <div className={classes.detalleFirma}>
                  CEO & Co-founder ELSA
                </div>
              </Stack>
            </div>
          </div>
          <div className={classes.fecha} style={fechaStyle}>
            {fecha || new Date().toLocaleDateString("es-ES")}
          </div>
          <Group>
            <Button
              onClick={downloadCertificate}
              className={classes.downloadCertificate}
              color="blue"
            >
              <Flex gap={5}>
                {t("actions.download") + " " + t("certificate")} <FaDownload />
              </Flex>
            </Button>
            <Button
              onClick={shareOnLinkedIn}
              className={classes.shareLinkedin}
              color="blue"
            >
              <Flex gap={5}>
                Compartir en LinkedIn <FaShareSquare />
              </Flex>
            </Button>
          </Group>
        </div>
      </Box>
    </Center>
  );
};
